import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  InputAdornment,
  makeStyles,
  MenuItem,
  Modal,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { Autocomplete, Pagination } from "@material-ui/lab";
import VisibilityIcon from "@material-ui/icons/Visibility";
import SearchIcon from "@material-ui/icons/Search";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { Image } from "app/routes/components/routes/Image";
import { useFileManager } from "hooks/useFileManager";
import { setPaginationByAmountAndSize } from "util/list-fns";
import { AirlineLogo } from "../airlines/airlineList/AilrlineLogo";
import { useDebounce } from "util/debounceRamda";
import {
  getAirlines,
  getFilteredBugs,
  getUsers,
  resolveBugReports,
  updateBugReportsStatus,
} from "./slice/bugReportedThunk";
import {
  selAirlineBugFilterSize,
  selAirlineBugTotalItems,
  selBugReportSummary,
  selAirlinebugFilterPage,
  setAirlineBugFilterPage,
} from "./slice/bugReportedSlice";

const statusOptions = [
  { value: "all", label: "All" },
  { value: "TODO", label: "Todo" },
  { value: "IN_PROGRESS", label: "In Progress" },
  { value: "RESOLVED", label: "Resolved" },
];

const statusLabels = statusOptions.reduce(
  (acc, item) => ({ ...acc, [item.value]: item.label }),
  {}
);

const statusColors = {
  TODO: "#f5a623",
  IN_PROGRESS: "#2196f3",
  RESOLVED: "#4caf50",
};

const normalizeStatus = (status) => {
  if (!status) return "TODO";
  const normalized = String(status).trim().toUpperCase().replace(/\s+/g, "_");
  if (normalized === "RESOLVE") return "RESOLVED";
  return normalized;
};

const getUserName = (user) =>
  [user?.first_name, user?.last_name].filter(Boolean).join(" ") || "-";

const formatDate = (date, format = "MM/DD/YYYY") =>
  date ? moment(date).format(format) : "-";

const buildQuery = (params) =>
  Object.entries(params)
    .filter(
      ([, value]) => value !== "" && value !== null && value !== undefined
    )
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join("&");

const BugReport = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { onDownload } = useFileManager();
  const bugReports = useSelector((state) => state.bugReport.bugReportedList);
  const airlines = useSelector((state) => state.bugReport.airlineList);
  const users = useSelector((state) => state.bugReport.usersList);
  const page = useSelector(selAirlinebugFilterPage);
  const size = useSelector(selAirlineBugFilterSize);
  const totalItems = useSelector(selAirlineBugTotalItems);
  const summary = useSelector(selBugReportSummary);

  const [detailOpen, setDetailOpen] = useState(false);
  const [resolveOpen, setResolveOpen] = useState(false);
  const [selectedBug, setSelectedBug] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [resolvedNote, setResolvedNote] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [params, setParams] = useState({
    page: 1,
    limit: size,
  });

  const queryParam = useMemo(() => buildQuery(params), [params]);
  const selectedRows = useMemo(
    () => bugReports.filter((item) => selectedIds.includes(item.id)),
    [bugReports, selectedIds]
  );
  const resolvableSelectedIds = useMemo(
    () =>
      selectedRows
        .filter((item) => normalizeStatus(item.status) !== "RESOLVED")
        .map((item) => item.id),
    [selectedRows]
  );
  const inProgressSelectedIds = useMemo(
    () =>
      selectedRows
        .filter((item) => {
          const status = normalizeStatus(item.status);
          return status !== "RESOLVED" && status !== "IN_PROGRESS";
        })
        .map((item) => item.id),
    [selectedRows]
  );
  const allResolvableIds = useMemo(
    () =>
      bugReports
        .filter((item) => normalizeStatus(item.status) !== "RESOLVED")
        .map((item) => item.id),
    [bugReports]
  );
  const pagination = {
    cur: page,
    all: setPaginationByAmountAndSize(size, totalItems),
  };

  useEffect(() => {
    dispatch(getAirlines());
    dispatch(getUsers());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getFilteredBugs(queryParam));
  }, [dispatch, queryParam]);

  useEffect(() => {
    setSelectedIds((prev) =>
      prev.filter((id) => bugReports.some((item) => item.id === id))
    );
  }, [bugReports]);

  const handleChangePage = (event, nextPage) => {
    dispatch(setAirlineBugFilterPage(nextPage));
    setParams((prev) => ({ ...prev, page: nextPage }));
  };

  const handleSelectAirline = useCallback((event, value) => {
    setParams(({ airline, ...rest }) =>
      value?.id ? { ...rest, airline: value.id, page: 1 } : { ...rest, page: 1 }
    );
  }, []);

  const handleSelectUsers = useCallback((event, value) => {
    setParams(({ created_by, ...rest }) =>
      value?.id
        ? { ...rest, created_by: value.id, page: 1 }
        : { ...rest, page: 1 }
    );
  }, []);

  const handleSelectedStatus = useCallback(({ target }) => {
    const { value } = target;
    setStatus(value);
    setParams(({ status, ...rest }) =>
      value === "all"
        ? { ...rest, page: 1 }
        : { ...rest, status: value, page: 1 }
    );
  }, []);

  const updateSearchParam = useCallback(
    (value) => {
      dispatch(setAirlineBugFilterPage(1));
      setParams(({ search, ...rest }) =>
        value ? { ...rest, search: value, page: 1 } : { ...rest, page: 1 }
      );
    },
    [dispatch]
  );

  const debouncedSearch = useDebounce(updateSearchParam, 400);

  const handleSearch = useCallback(
    ({ target: { value } }) => {
      setSearch(value);
      debouncedSearch(value.trim());
    },
    [debouncedSearch]
  );

  const handleOpenDetail = (bug) => {
    setSelectedBug(bug);
    setDetailOpen(true);
  };

  const handleCloseDetail = () => {
    setDetailOpen(false);
    setSelectedBug(null);
  };

  const handleToggleRow = (bug) => {
    if (normalizeStatus(bug.status) === "RESOLVED") return;
    setSelectedIds((prev) =>
      prev.includes(bug.id)
        ? prev.filter((selectedId) => selectedId !== bug.id)
        : [...prev, bug.id]
    );
  };

  const handleToggleAll = ({ target: { checked } }) => {
    setSelectedIds(checked ? allResolvableIds : []);
  };

  const handleOpenResolveModal = () => {
    if (!resolvableSelectedIds.length) return;
    setResolveOpen(true);
  };

  const handleCloseResolveModal = () => {
    setResolveOpen(false);
    setResolvedNote("");
  };

  const handleResolveSelected = () => {
    dispatch(
      resolveBugReports(resolvableSelectedIds, resolvedNote, queryParam, () => {
        setSelectedIds([]);
        handleCloseResolveModal();
      })
    );
  };

  const handleMarkInProgress = () => {
    if (!inProgressSelectedIds.length) return;
    dispatch(
      updateBugReportsStatus(
        inProgressSelectedIds,
        "IN_PROGRESS",
        queryParam,
        () => setSelectedIds([])
      )
    );
  };

  const renderAttachments = (bug) => {
    if (!bug?.attachments?.length) {
      return <Typography variant="body2">No attachments</Typography>;
    }

    return (
      <div className={classes.attachments}>
        {bug.attachments.map((attachment) => {
          const fileId = attachment?.id || attachment;
          const mimetype = attachment?.mimetype || "image/jpeg";
          return (
            <div className={classes.attachment} key={fileId}>
              <Image
                onClick={() => onDownload(fileId)}
                imageId={fileId}
                mimeTypeFile={mimetype}
                objectFit="cover"
              />
              <Typography className={classes.attachmentName}>
                {attachment?.originalname || fileId}
              </Typography>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Box>
      <div className={classes.summaryGrid}>
        <Paper className={classes.summaryCard}>
          <Typography className={classes.summaryTitle}>Total Bugs</Typography>
          <Typography className={classes.summaryValue}>
            {summary?.total ?? totalItems}
          </Typography>
        </Paper>
        <Paper className={classes.summaryCard}>
          <Typography className={classes.summaryTitle}>In Progress</Typography>
          <Typography className={classes.summaryValue}>
            {summary?.in_progress ?? 0}
          </Typography>
        </Paper>
        <Paper className={classes.summaryCard}>
          <Typography className={classes.summaryTitle}>Resolved</Typography>
          <Typography className={classes.summaryValue}>
            {summary?.resolved ?? 0}
          </Typography>
        </Paper>
      </div>

      <Paper className={classes.filterBar}>
        <FormControl variant="outlined" className={classes.filterControl}>
          <Autocomplete
            clearOnBlur={false}
            options={airlines}
            onChange={handleSelectAirline}
            renderInput={(params) => (
              <TextField {...params} label="Filter Airline" />
            )}
            getOptionLabel={(option) => option?.name || ""}
            renderOption={(option) => option?.name || "-"}
          />
        </FormControl>
        <FormControl variant="outlined" className={classes.filterControl}>
          <Autocomplete
            clearOnBlur={false}
            options={users}
            onChange={handleSelectUsers}
            renderInput={(params) => (
              <TextField {...params} label="Filter User" />
            )}
            getOptionLabel={(option) => getUserName(option)}
            renderOption={(option) => getUserName(option)}
          />
        </FormControl>
        <FormControl variant="outlined" className={classes.statusFilter}>
          <InputLabel>Status</InputLabel>
          <Select value={status} onChange={handleSelectedStatus} label="Status">
            {statusOptions.map((option) => (
              <MenuItem value={option.value} key={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          placeholder="Search bugs..."
          value={search}
          onChange={handleSearch}
          className={classes.searchInput}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="disabled" />
              </InputAdornment>
            ),
          }}
        />
        <Button
          color="primary"
          variant="outlined"
          disabled={!inProgressSelectedIds.length}
          onClick={handleMarkInProgress}
          className={classes.inProgressButton}
        >
          In Progress
        </Button>
        <Button
          color="primary"
          variant="contained"
          disabled={!resolvableSelectedIds.length}
          onClick={handleOpenResolveModal}
          className={classes.actionButton}
        >
          Resolve
        </Button>
      </Paper>

      <Paper elevation={5}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    indeterminate={
                      selectedIds.length > 0 &&
                      selectedIds.length < allResolvableIds.length
                    }
                    checked={
                      allResolvableIds.length > 0 &&
                      selectedIds.length === allResolvableIds.length
                    }
                    onChange={handleToggleAll}
                  />
                </TableCell>
                <TableCell align="center">Number</TableCell>
                <TableCell align="center">Title</TableCell>
                <TableCell align="center">Airline</TableCell>
                <TableCell align="center">Creator</TableCell>
                <TableCell align="center">Create Date</TableCell>
                <TableCell align="center">Priority</TableCell>
                <TableCell align="center">Platform</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bugReports.map((item) => {
                const isSelected = selectedIds.includes(item.id);
                const normalizedStatus = normalizeStatus(item.status);
                const isResolved = normalizedStatus === "RESOLVED";
                return (
                  <TableRow
                    key={item.id}
                    hover
                    selected={isSelected}
                    onClick={() => handleToggleRow(item)}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        color="primary"
                        checked={isSelected}
                        disabled={isResolved}
                        onClick={(event) => event.stopPropagation()}
                        onChange={() => handleToggleRow(item)}
                      />
                    </TableCell>
                    <TableCell align="center">{item.number || "-"}</TableCell>
                    <TableCell align="center">{item.title || "-"}</TableCell>
                    <TableCell align="center">
                      <div className={classes.airlineCell}>
                        <AirlineLogo imageId={item.airline?.logo} />
                        <span>{item.airline?.name || "-"}</span>
                      </div>
                    </TableCell>
                    <TableCell align="center">
                      {getUserName(item.created_by)}
                    </TableCell>
                    <TableCell align="center">
                      {formatDate(item.created_time)}
                    </TableCell>
                    <TableCell align="center">{item.priority || "-"}</TableCell>
                    <TableCell align="center">{item.platform || "-"}</TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={
                          statusLabels[normalizedStatus] ||
                          item.status ||
                          "Todo"
                        }
                        className={classes.statusChip}
                        style={{
                          backgroundColor:
                            statusColors[normalizedStatus] || "#9e9e9e",
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View details">
                        <IconButton
                          color="primary"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleOpenDetail(item);
                          }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!bugReports.length && (
                <TableRow>
                  <TableCell align="center" colSpan={10}>
                    No bug reports found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Pagination
          count={pagination.all}
          page={pagination.cur}
          shape="rounded"
          color="primary"
          onChange={handleChangePage}
          className={classes.pagination}
        />
      </Paper>

      <Modal open={detailOpen} onClose={handleCloseDetail}>
        <Box className={classes.detailModal}>
          <Typography variant="h6">Bug Report Detail</Typography>
          <div className={classes.detailGrid}>
            <Typography>
              <strong>Number:&nbsp;</strong>
              {selectedBug?.number || "-"}
            </Typography>
            <Typography>
              <strong>Title:&nbsp;</strong>
              {selectedBug?.title || "-"}
            </Typography>
            <Typography>
              <strong>Airline:&nbsp;</strong>
              {selectedBug?.airline?.name || "-"}
            </Typography>
            <Typography>
              <strong>Created By:&nbsp;</strong>
              {getUserName(selectedBug?.created_by)}
            </Typography>
            <Typography>
              <strong>Created Time:&nbsp;</strong>
              {formatDate(selectedBug?.created_time, "MM/DD/YYYY - HH:mm:ss")}
            </Typography>
            <Typography>
              <strong>Status:&nbsp;</strong>
              {statusLabels[normalizeStatus(selectedBug?.status)] ||
                selectedBug?.status ||
                "Todo"}
            </Typography>
            <Typography>
              <strong>Priority:&nbsp;</strong>
              {selectedBug?.priority || "-"}
            </Typography>
            <Typography>
              <strong>Platform:&nbsp;</strong>
              {selectedBug?.platform || "-"}
            </Typography>
            <Typography>
              <strong>Browser:&nbsp;</strong>
              {selectedBug?.os_info?.browser || "-"}
            </Typography>
            <Typography>
              <strong>OS:&nbsp;</strong>
              {selectedBug?.os_info?.os || "-"}
            </Typography>
            <Typography>
              <strong>Resolved By:&nbsp;</strong>
              {getUserName(selectedBug?.resolved_by)}
            </Typography>
            <Typography>
              <strong>Resolved Time:&nbsp;</strong>
              {formatDate(selectedBug?.resolved_date, "MM/DD/YYYY - HH:mm:ss")}
            </Typography>
          </div>
          <Typography className={classes.detailBlock}>
            <strong>Description:&nbsp;</strong>
            {selectedBug?.description || "-"}
          </Typography>
          <Typography className={classes.detailBlock}>
            <strong>User Agent:&nbsp;</strong>
            {selectedBug?.user_agent || "-"}
          </Typography>
          <Typography className={classes.detailBlock}>
            <strong>Resolved Note:&nbsp;</strong>
            {selectedBug?.resolved_note || "-"}
          </Typography>
          <Typography className={classes.detailBlock}>
            <strong>Attachments:&nbsp;</strong>
          </Typography>
          {renderAttachments(selectedBug)}
        </Box>
      </Modal>

      <Modal open={resolveOpen} onClose={handleCloseResolveModal}>
        <Box className={classes.resolveModal}>
          <Typography variant="h6">Resolve selected bug reports</Typography>
          <Typography variant="body2" color="textSecondary">
            {resolvableSelectedIds.length} selected report(s) will be resolved.
          </Typography>
          <TextField
            variant="outlined"
            label="Resolve Note"
            value={resolvedNote}
            onChange={(event) => setResolvedNote(event.target.value)}
            multiline
            rows={5}
            fullWidth
            className={classes.resolveNote}
          />
          <div className={classes.modalActions}>
            <Button onClick={handleCloseResolveModal}>Cancel</Button>
            <Button
              color="primary"
              variant="contained"
              disabled={!resolvedNote.trim()}
              onClick={handleResolveSelected}
            >
              Resolve
            </Button>
          </div>
        </Box>
      </Modal>
    </Box>
  );
};

export default BugReport;

const useStyles = makeStyles((theme) => ({
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(160px, 1fr))",
    gap: theme.spacing(3),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
      gap: theme.spacing(1.5),
    },
  },
  summaryCard: {
    minHeight: 96,
    padding: theme.spacing(2, 3),
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.04)",
  },
  summaryTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  summaryValue: {
    fontSize: 28,
    fontWeight: 700,
    color: theme.palette.text.primary,
    lineHeight: 1,
  },
  filterBar: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(2),
  },
  searchInput: {
    width: 300,
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  filterControl: {
    width: 220,
  },
  statusFilter: {
    width: 150,
  },
  inProgressButton: {
    marginLeft: "auto",
  },
  actionButton: {},
  airlineCell: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  statusChip: {
    color: theme.palette.common.white,
    fontWeight: 600,
  },
  pagination: {
    padding: theme.spacing(2),
    display: "flex",
    justifyContent: "flex-end",
  },
  detailModal: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 900,
    maxWidth: "calc(100vw - 32px)",
    maxHeight: "calc(100vh - 32px)",
    overflowY: "auto",
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[14],
    padding: theme.spacing(4),
    borderRadius: theme.shape.borderRadius,
  },
  detailGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: theme.spacing(1.5, 3),
    marginTop: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  detailBlock: {
    marginTop: theme.spacing(2),
    wordBreak: "break-word",
  },
  attachments: {
    display: "flex",
    gap: theme.spacing(1.5),
    overflowX: "auto",
    marginTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  attachment: {
    flex: "0 0 160px",
    height: 140,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1),
  },
  attachmentName: {
    fontSize: 12,
    marginTop: theme.spacing(0.5),
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  resolveModal: {
    position: "absolute",
    top: "35%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 520,
    maxWidth: "calc(100vw - 32px)",
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[14],
    padding: theme.spacing(4),
    borderRadius: theme.shape.borderRadius,
  },
  resolveNote: {
    marginTop: theme.spacing(3),
  },
  modalActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    marginTop: theme.spacing(3),
  },
}));
